(() => {
  'use strict';
  if (customElements.get('deferred-social-video')) return;

  class DeferredSocialVideo extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.events) return;
        this.events = new AbortController();
        const on = (target, type, handler) => target?.addEventListener(type, handler, {signal: this.events.signal});
        this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.near = false;
        this.visible = false;
        this.userPaused = false;
        this.userStarted = false;
        this.failed = false;
        this.toggle = this.closest('.column-item__image-wrapper--inner')?.querySelector('[data-social-video-toggle]');
        if (this.toggle) this.toggle.hidden = false;
        on(this.toggle, 'click', () => {
          if (this.video && !this.video.paused) {
            this.userPaused = true;
            this.video.pause();
          } else {
            this.userPaused = false;
            this.userStarted = true;
            this.failed = false;
            this.loadVideo();
            this.playVideo();
          }
        });
        on(document, 'visibilitychange', () => this.sync());
        on(this.motionQuery, 'change', () => {
          this.userStarted = false;
          this.sync();
        });
        on(document, 'shopify:section:unload', event => {
          if (event.target.contains(this)) this.disconnect();
        });
        if ('IntersectionObserver' in window) {
          this.preloadObserver = new IntersectionObserver(entries => {
            this.near = entries[entries.length - 1].isIntersecting;
            this.sync();
          }, {rootMargin: '300px 0px', threshold: 0.01});
          this.visibilityObserver = new IntersectionObserver(entries => {
            this.visible = entries[entries.length - 1].isIntersecting;
            this.sync();
          }, {threshold: 0.01});
          this.preloadObserver.observe(this);
          this.visibilityObserver.observe(this);
        }
        this.updateControl();
      });
    }

    loadVideo() {
      if (this.video || this.failed || document.hidden) return;
      const content = this.querySelector('template')?.content.cloneNode(true);
      const video = content?.querySelector('video');
      if (!video) return;
      this.video = video;
      const signal = this.events.signal;
      video.addEventListener('loadeddata', () => this.setAttribute('data-video-ready', ''), {signal, once: true});
      video.addEventListener('play', () => this.updateControl(), {signal});
      video.addEventListener('pause', () => this.updateControl(), {signal});
      video.addEventListener('error', () => {
        this.failed = true;
        this.releaseVideo();
        this.updateControl();
      }, {signal});
      this.appendChild(content);
      video.preload = 'metadata';
      video.load();
    }

    playVideo() {
      if (!this.video || document.hidden || this.userPaused ||
          (this.motionQuery.matches && !this.userStarted)) return;
      this.video.play()?.catch(() => this.updateControl());
    }

    sync() {
      if (document.hidden || this.userPaused || (this.motionQuery.matches && !this.userStarted)) {
        this.video?.pause();
        return;
      }
      if (this.near || this.visible) this.loadVideo();
      if (this.visible) this.playVideo();
      else this.video?.pause();
    }

    updateControl() {
      if (!this.toggle) return;
      const playing = this.video && !this.video.paused;
      this.toggle.setAttribute('aria-label', playing ? this.toggle.dataset.pauseLabel : this.toggle.dataset.playLabel);
      this.toggle.querySelector('span').textContent = playing ? 'Ⅱ' : '▶';
    }

    releaseVideo() {
      if (!this.video) return;
      this.video.pause();
      this.video.removeAttribute('src');
      this.video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
      this.video.load();
      this.video.remove();
      this.video = null;
      this.removeAttribute('data-video-ready');
    }

    disconnect() {
      this.events?.abort();
      this.events = null;
      this.preloadObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.releaseVideo();
      if (this.toggle) this.toggle.hidden = true;
    }

    disconnectedCallback() { this.disconnect(); }
  }

  customElements.define('deferred-social-video', DeferredSocialVideo);
})();
